// DCIC-07 Facility Status Update tab — status updates (R05 → R04) and facility
// climate assessments (R04/R03 → R02). R05 only ever works with status updates.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NlimsDataTable from '../../shared/NlimsDataTable/NlimsDataTable';
import { useAuth } from '../../../context/AuthContext';
import { useNotifications } from '../../../context/NotificationContext';
import { useCollectionSync } from '../../../hooks/useCollectionSync';
import { facilityStatusUpdatesApi } from '../../../api/facilityStatusUpdatesApi';
import { FUNCTIONAL_STATUS_OPTIONS } from '../../../api/facilitiesApi';
import { ACT, dataStatusBadgeHtml, functionalBadgeHtml, humanizeWorkflowStatus } from '../shared/FacilityWorkflow';
import { ActionDropdown, anchorRect, RejectNotesModal, DeleteConfirmModal, QuickActionOverlay, WORKFLOW_RULES } from '../../shared/RowActionMenu';
import {
  ASSESSMENT_OUTCOME_OPTIONS, ASSESSMENT_SCOPE_OPTIONS, ASSESSMENT_TYPE_OPTIONS, EMISSIONS_CLASSIFICATION_OPTIONS,
  UPDATE_TYPE_OPTIONS, VERIFICATION_RESULT_OPTIONS, labelOf,
} from '../shared/climateOptions';

const COLUMNS = [
  { title: 'Facility',      data: 'facilityName', sortable: true },
  { title: 'State',         data: 'state',        sortable: true },
  { title: 'LGA',           data: 'lga',          sortable: true },
  { title: 'Update Type',   data: 'updateTypeLabel', sortable: true },
  { title: 'Functional Status / Assessment', data: 'detailLabel', sortable: true,
    render: (value, type, row) => (row?.update_type === 'status_update' ? functionalBadgeHtml(value, type) : (value ?? '')) },
  { title: 'Scope',         data: 'scopeLabel',   sortable: true },
  { title: 'Result',        data: 'resultLabel',  sortable: true },
  { title: 'Date',          data: 'dateLabel',    sortable: true },
  { title: 'Outcome',       data: 'outcomeLabel', sortable: true },
  { title: 'Data Status',   data: 'statusLabel',  sortable: true, render: dataStatusBadgeHtml },
  { title: 'Action',        data: null,           sortable: false, render: ACT },
];

function toRow(record) {
  const isClimate = record.update_type === 'climate_assessment';
  return {
    ...record,
    facilityName: record.facility_name ?? '',
    updateTypeLabel: labelOf(UPDATE_TYPE_OPTIONS, record.update_type),
    detailLabel: isClimate ? labelOf(ASSESSMENT_TYPE_OPTIONS, record.assessment_type) : labelOf(FUNCTIONAL_STATUS_OPTIONS, record.functional_status),
    dateLabel: String((isClimate ? record.assessment_date : null) ?? record.created_at ?? '').slice(0, 10),
    outcomeLabel: isClimate ? labelOf(ASSESSMENT_OUTCOME_OPTIONS, record.assessment_outcome) : '—',
    scopeLabel: isClimate ? labelOf(ASSESSMENT_SCOPE_OPTIONS, record.assessment_scope) : '—',
    resultLabel: !isClimate ? '—'
      : record.emissions_classification ? labelOf(EMISSIONS_CLASSIFICATION_OPTIONS, record.emissions_classification)
        : record.verification_result ? labelOf(VERIFICATION_RESULT_OPTIONS, record.verification_result) : '—',
    statusLabel: humanizeWorkflowStatus(record.data_status),
  };
}

export default function FacilityStatusUpdates() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { pushWorkflowNotification } = useNotifications();
  const roleId = user?.roleId ?? '';
  const isR05 = roleId === 'R05';
  const canCreate = ['R01', 'R03', 'R04', 'R05'].includes(roleId);
  const [records, setRecords] = useState([]);
  const [error, setError] = useState('');
  const [actionMenu, setActionMenu] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const { items } = await facilityStatusUpdatesApi.listAll();
      setRecords(items);
    } catch (e) {
      setError(e?.message ?? 'Failed to load facility status updates.');
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useCollectionSync(facilityStatusUpdatesApi.collectionKey, load);

  const rows = useMemo(
    () => records.filter((r) => !isR05 || r.update_type !== 'climate_assessment').map(toRow),
    [records, isR05]
  );

  const runTransition = useCallback(async (row, action, notes) => {
    const labels = { submit: 'Submitting...', validate: 'Validating...', approve: 'Approving...', reject: 'Rejecting...' };
    const statusByAction = { submit: 'submitted', validate: 'validated', approve: 'approved', reject: 'rejected' };
    setBusy(labels[action]);
    setError('');
    try {
      await facilityStatusUpdatesApi[action](row.uuid, notes);
      pushWorkflowNotification({
        moduleKey: 'infrastructure', roleCan: WORKFLOW_RULES.facilityStatusUpdates.roleCan, newStatus: statusByAction[action],
        actorRoleId: roleId, recordLabel: `${row.facilityName} — ${row.updateTypeLabel}`, notes,
      });
      await load();
    } catch (e) {
      setError(e?.message ?? `Failed to ${action} record.`);
    } finally {
      setBusy('');
    }
  }, [load, pushWorkflowNotification, roleId]);

  const closeMenu = () => { const r = actionMenu?.row; setActionMenu(null); return r; };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="text-xs text-gray-500">
          {isR05 ? 'Update the functional status of facilities in your LGA.' : 'Status updates and climate assessments recorded against registered facilities.'}
        </p>
        {canCreate && (
          <button type="button" onClick={() => navigate('/infrastructure/new?subModule=status')}
            className="h-10 shrink-0 rounded-full bg-green-600 px-5 text-sm font-semibold text-white hover:bg-green-700">
            {isR05 ? 'Update Status' : 'New Status Update'}
          </button>
        )}
      </div>

      {error && <div className="mb-3 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <NlimsDataTable
        tableId="facility-status-updates-table"
        columns={COLUMNS}
        data={rows}
        defaultSort={[7, 'desc']}
        pageLength={25}
        onRowAction={(action, row, meta) => { if (action === 'menu' || action === 'view') setActionMenu({ row, rect: anchorRect(meta?.button) }); }}
      />

      {actionMenu && !busy && (
        <ActionDropdown
          row={actionMenu.row}
          rect={actionMenu.rect}
          roleId={roleId}
          rules={WORKFLOW_RULES.facilityStatusUpdates}
          editableStatuses={isR05 && actionMenu.row.update_type === 'climate_assessment' ? { R05: [] } : undefined}
          onClose={() => setActionMenu(null)}
          onView={() => { const r = closeMenu(); navigate(`/infrastructure/view/${r.uuid}?subModule=status`, { state: { row: r } }); }}
          onEdit={() => { const r = closeMenu(); navigate(`/infrastructure/edit/${r.uuid}?subModule=status`, { state: { row: r } }); }}
          onSubmit={() => runTransition(closeMenu(), 'submit')}
          onValidate={() => runTransition(closeMenu(), 'validate')}
          onApprove={() => runTransition(closeMenu(), 'approve')}
          onReject={() => setRejectTarget(closeMenu())}
          onDelete={() => setDeleteTarget(closeMenu())}
        />
      )}

      {rejectTarget && (
        <RejectNotesModal
          onConfirm={async (notes) => { await runTransition(rejectTarget, 'reject', notes); setRejectTarget(null); }}
          onClose={() => setRejectTarget(null)}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          row={deleteTarget}
          entityLabel={(r) => `${r?.facilityName ?? ''} — ${r?.updateTypeLabel ?? ''}`}
          onConfirm={async () => {
            await facilityStatusUpdatesApi.remove(deleteTarget.uuid);
            setDeleteTarget(null);
            await load();
          }}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {busy && <QuickActionOverlay label={busy} />}
    </div>
  );
}
